/**
 * メインレイアウト (レフトドロワー + タイトルバー + コンテンツ) - Neo-Futuristic
 */
import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { userApi } from '../services/droneOpsApi';

const NAV_ITEMS = [
  { path: '/',     icon: 'calendar_month', label: 'フライト予約' },
  { path: '/dips', icon: 'link',           label: '登録機体一覧', iconRotate: true },
];

const ROLE_LABELS = {
  admin:    '管理者',
  operator: 'オペレーター',
  viewer:   '閲覧者',
};

// Material Symbols ラッパー
function MSIcon({ name, className = 'text-xl' }) {
  return <span className={`material-symbols-outlined leading-none select-none ${className}`}>{name}</span>;
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [profile, setProfile] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    userApi.me().then(setProfile).catch(() => {});
  }, []);

  // ユーザーメニュー外クリックで閉じる
  useEffect(() => {
    function handleOutsideClick(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    }
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [userMenuOpen]);

  const handleLogout = () => {
    setUserMenuOpen(false);
    logout();
    navigate('/login');
  };

  const displayName = profile?.displayName || user?.email || 'User';
  const roleLabel   = ROLE_LABELS[profile?.role] || profile?.role || '';

  return (
    <div className="h-screen bg-background text-on-surface flex overflow-hidden">

      {/* ===== Left Drawer ===== */}
      <aside
        className={`
          fixed left-0 top-0 h-full z-40 flex flex-col
          bg-slate-950/95 border-r border-blue-500/20
          shadow-[5px_0_20px_rgba(0,0,0,0.5)]
          transition-all duration-300
          ${sidebarCollapsed ? 'w-16' : 'w-64'}
        `}
      >
        {/* サービスアイコン + AIRGROW */}
        <div className={`flex items-center gap-3 px-3 py-4 border-b border-blue-900/40 ${sidebarCollapsed ? 'justify-center' : ''}`}>
          <div className="w-9 h-9 flex-shrink-0 bg-primary-container rounded-sm flex items-center justify-center border border-primary/30">
            <MSIcon name="flight" className="text-white text-lg" />
          </div>
          {!sidebarCollapsed && (
            <div className="min-w-0">
              <h2 className="font-label-caps text-base font-bold text-blue-400 tracking-widest uppercase leading-none">
                AIRGROW
              </h2>
              <p className="text-[10px] text-slate-500 tracking-tight mt-0.5">FLIGHT MANAGEMENT</p>
            </div>
          )}
        </div>

        {/* ナビゲーション */}
        <nav className="flex-1 py-3 px-2 space-y-0.5" aria-label="メインナビゲーション">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) =>
                `flex items-center gap-3 py-4 px-3 transition-colors duration-200
                font-label-caps text-sm font-bold tracking-widest
                ${sidebarCollapsed ? 'justify-center' : ''}
                ${isActive
                  ? 'border-l-4 border-blue-500 bg-blue-600/10 text-blue-400 pl-2'
                  : 'border-l-4 border-transparent text-slate-400 hover:bg-blue-900/20 hover:text-white'
                }`
              }
              title={item.label}
            >
              <MSIcon name={item.icon} className={`text-3xl flex-shrink-0 ${item.iconRotate ? 'inline-block rotate-[135deg]' : ''}`} />
              {!sidebarCollapsed && (
                <span className="truncate">{item.label}</span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* ハンバーガー（ドロワー開閉） */}
        <div className="border-t border-blue-900/40 p-2">
          <button
            className={`
              w-full flex items-center gap-2 py-2 px-3 rounded
              text-slate-500 hover:bg-blue-900/20 hover:text-blue-300
              transition-colors duration-200
              font-label-caps text-sm font-bold tracking-widest
              ${sidebarCollapsed ? 'justify-center' : ''}
            `}
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            aria-label={sidebarCollapsed ? 'ドロワーを展開' : 'ドロワーを格納'}
          >
            <MSIcon name="menu" className="text-3xl flex-shrink-0" />
            {!sidebarCollapsed && (
              <span className="flex items-center gap-1">
                <span className="text-blue-500/70 text-base leading-none">‹</span>
                <span>メニューを閉じる</span>
              </span>
            )}
            {sidebarCollapsed && (
              <span className="text-blue-500/70 text-base leading-none">›</span>
            )}
          </button>
        </div>
      </aside>

      {/* ===== 右ペイン ===== */}
      <div
        className={`flex flex-col flex-1 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-64'}`}
      >

        {/* ===== Title Bar ===== */}
        <header
          className="sticky top-0 z-30 flex items-center justify-between
            h-14 px-5
            bg-slate-950/80 backdrop-blur-md
            border-b border-blue-500/30
            shadow-[0_0_15px_rgba(0,104,183,0.15)]"
        >
          {/* 左: アプリ名 */}
          <h1 className="font-label-caps text-xl font-bold tracking-[0.2em] italic text-blue-500 uppercase">
            FLIGHT PLANNER
          </h1>

          {/* 右: 通知 / ヘルプ / セパレータ / ユーザー */}
          <div className="flex items-center gap-3">
            {/* 通知アイコン */}
            <button
              className="p-2 text-slate-500 hover:text-blue-300 hover:bg-blue-900/20 rounded transition-colors duration-200"
              aria-label="通知"
            >
              <MSIcon name="notifications" className="text-xl" />
            </button>

            {/* ヘルプアイコン */}
            <button
              className="p-2 text-slate-500 hover:text-blue-300 hover:bg-blue-900/20 rounded transition-colors duration-200"
              aria-label="ヘルプ"
            >
              <MSIcon name="help_outline" className="text-xl" />
            </button>

            {/* 縦セパレータ */}
            <div className="h-6 w-px bg-blue-900/60 mx-2" aria-hidden="true" />

            {/* ユーザーエリア（ドロップダウントリガー） */}
            <div className="relative" ref={userMenuRef}>
              <button
                className="flex items-center gap-2 px-2 py-1.5 rounded
                  text-slate-300 hover:text-white hover:bg-blue-900/20
                  transition-colors duration-200"
                onClick={() => setUserMenuOpen((v) => !v)}
                aria-expanded={userMenuOpen}
                aria-haspopup="true"
              >
                <span className="font-label-caps text-sm tracking-wider hidden sm:block">
                  {displayName}
                </span>
                <div className="w-7 h-7 rounded-full bg-primary-container border border-primary/30 flex items-center justify-center flex-shrink-0">
                  <MSIcon name="account_circle" className="text-white text-lg" />
                </div>
              </button>

              {/* ユーザードロップダウン */}
              {userMenuOpen && (
                <div
                  className="absolute right-0 top-full mt-2 w-56
                    bg-surface-container-high border border-blue-500/20
                    shadow-[0_8px_32px_rgba(0,0,0,0.5)]
                    z-50 overflow-hidden"
                >
                  {/* ユーザー情報 */}
                  <div className="px-4 py-3 border-b border-blue-900/40">
                    <p className="font-label-caps text-sm font-bold tracking-widest text-blue-400 truncate">
                      {displayName}
                    </p>
                    {roleLabel && (
                      <p className="text-[11px] text-slate-400 mt-0.5">{roleLabel}</p>
                    )}
                    {user?.email && (
                      <p className="text-[11px] text-slate-500 truncate mt-0.5">{user.email}</p>
                    )}
                  </div>

                  {/* ログアウト */}
                  <button
                    className="w-full flex items-center gap-3 px-4 py-3
                      font-label-caps text-[11px] tracking-widest
                      text-slate-400 hover:bg-blue-900/20 hover:text-error
                      transition-colors duration-200"
                    onClick={handleLogout}
                  >
                    <MSIcon name="logout" className="text-base" />
                    <span>LOGOUT</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* ===== コンテンツエリア ===== */}
        <main className="flex-1 overflow-auto py-6 px-12">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
